import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Briefcase, ClipboardList, Users } from 'lucide-react';
import type { EmployeeTaskSummary } from '@pms/shared';
import { Tabs } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/useAuth';
import { tasksService } from '@/services/tasks.service';
import { additionalTasksService } from '@/services/additional-tasks.service';
import { EmployeeCard } from '@/components/tasks/EmployeeCard';
import { EmployeeTasksDrawer } from '@/components/tasks/EmployeeTasksDrawer';
import { TaskRow, AdditionalTaskRow } from '@/components/tasks/TaskRow';
import { TaskDetailDrawer } from '@/components/tasks/TaskDetailDrawer';

export function AssignedByMeTab() {
  const { user } = useAuth();
  const [sub, setSub] = useState<'employee' | 'task' | 'additional'>('employee');
  const [selectedEmp, setSelectedEmp] = useState<EmployeeTaskSummary | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  const employeesQ = useQuery({
    queryKey: ['tasks', 'assigned-by-me', 'employees'],
    queryFn: () => tasksService.assignedByMeEmployees(),
    enabled: sub === 'employee',
  });
  const tasksQ = useQuery({
    queryKey: ['tasks', 'assigned-by-me', 'tasks'],
    queryFn: () => tasksService.assignedByMe(),
    enabled: sub === 'task',
  });
  const additionalQ = useQuery({
    queryKey: ['additional-tasks', 'assigned-by-me', user?.id],
    queryFn: () => additionalTasksService.list({ assigned_by: user!.id, limit: 100 }),
    enabled: sub === 'additional' && !!user,
  });

  return (
    <div className="space-y-4">
      <Tabs
        active={sub}
        onChange={(k) => setSub(k as 'employee' | 'task' | 'additional')}
        tabs={[
          { key: 'employee', label: <span className="flex items-center gap-1.5"><Users className="h-4 w-4" />By Employee {employeesQ.data?.length ?? ''}</span> },
          { key: 'task', label: <span className="flex items-center gap-1.5"><ClipboardList className="h-4 w-4" />By Task {tasksQ.data?.length ?? ''}</span> },
          { key: 'additional', label: <span className="flex items-center gap-1.5"><Briefcase className="h-4 w-4" />Additional {additionalQ.data?.pagination.total ?? ''}</span> },
        ]}
      />

      {sub === 'employee' && (
        <>
          {employeesQ.isLoading && (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {[...Array(6)].map((_, i) => <div key={i} className="h-44 animate-pulse rounded-xl bg-muted/50" />)}
            </div>
          )}
          {employeesQ.data && employeesQ.data.length === 0 && (
            <div className="rounded-xl border border-dashed border-border/60 p-10 text-center text-sm text-muted-foreground">You haven’t assigned any tasks yet.</div>
          )}
          {employeesQ.data && employeesQ.data.length > 0 && (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {employeesQ.data.map((emp) => <EmployeeCard key={emp.user_id} emp={emp} onClick={() => setSelectedEmp(emp)} />)}
            </div>
          )}
        </>
      )}

      {sub === 'task' && (
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
          {tasksQ.isLoading && <div className="space-y-px p-1">{[...Array(5)].map((_, i) => <div key={i} className="h-16 animate-pulse rounded bg-muted/40" />)}</div>}
          {tasksQ.data && tasksQ.data.length === 0 && <div className="p-10 text-center text-sm text-muted-foreground">No tasks created by you.</div>}
          {tasksQ.data?.map((t) => <TaskRow key={t.id} task={t} onClick={() => setDetailId(t.id)} />)}
        </div>
      )}

      {sub === 'additional' && (
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
          {additionalQ.isLoading && <div className="space-y-px p-1">{[...Array(3)].map((_, i) => <div key={i} className="h-16 animate-pulse rounded bg-muted/40" />)}</div>}
          {additionalQ.data && additionalQ.data.data.length === 0 && (
            <div className="flex flex-col items-center gap-2 p-12 text-center text-sm text-muted-foreground">
              <Briefcase className="h-8 w-8 opacity-40" /> No additional tasks
            </div>
          )}
          {additionalQ.data?.data.map((a) => <AdditionalTaskRow key={a.id} item={a} />)}
        </div>
      )}

      <EmployeeTasksDrawer employee={selectedEmp} open={!!selectedEmp} onOpenChange={(o) => !o && setSelectedEmp(null)} />
      <TaskDetailDrawer taskId={detailId} open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)} />
    </div>
  );
}
